import React, { ReactNode, useState } from 'react';
import classNames from 'classnames';
import { concatUrl, getAssetsPrefix, isNil } from '@/utils';
import { AssetDetailIcon } from './components';
import { AssetItem } from '@/type';
import { Input, message } from '@easyv/react-components';
import { useUpdateUserAsset } from '@/hooks/AssetList';

interface AssetBaseProps {
  icon?: ReactNode;
  data: AssetItem;
  defaultThumb?: string;
  canDrag?: boolean;
  canRename?: boolean;
  hideDetail?: boolean;
  hoverRender?: ReactNode;
  onRightClick?: (style: React.CSSProperties, selectedItem: AssetItem) => void;
}

export function AssetBase(props: AssetBaseProps) {
  const {
    icon = null,
    canDrag = false,
    canRename = false,
    hideDetail = false,
    data,
    hoverRender,
    onRightClick,
  } = props;
  const { id, name, type } = data;
  const thumb = data.thumb ? concatUrl(getAssetsPrefix(), data.thumb) : props.defaultThumb;
  const [isDragging, setIsDragging] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const updateAsset = useUpdateUserAsset();

  const handleNameChange = (
    e: React.KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement>,
  ) => {
    setIsEditing(false);
    const v = e.currentTarget.value;
    if (v === '') {
      message.error('名称不能为空');
      return;
    }
    if (v === name) {
      return;
    }
    updateAsset?.({
      assetId: id,
      name: v,
    });
  };

  return (
    <div key={id} data-id={id} className='flex items-center justify-center flex-col w-full'>
      <div
        draggable={canDrag}
        style={{
          backgroundImage: `url(${thumb})`,
        }}
        className='relative group bg-center bg-no-repeat bg-cover bg-FFFFFF0A rounded-xl cursor-pointer w-full h-full aspect-square border border-transparent'
        onDragStart={(e) => {
          e.dataTransfer.setData(
            'application/json',
            JSON.stringify({
              id,
              type,
            }),
          );
          e.dataTransfer.setDragImage(e.currentTarget, 40, 40);
          setIsDragging(true);
        }}
        onDragEnd={() => {
          setIsDragging(false);
        }}
        onContextMenu={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onRightClick?.(
            {
              top: e.clientY - 10,
              left: e.clientX + 1,
            },
            data,
          );
        }}
      >
        {!isDragging && (
          <>
            <div
              className={classNames(
                'absolute items-center justify-center -left-[1px] -right-[1px] -top-[1px] -bottom-[1px] hidden group-hover:flex rounded-xl aspect-square',
                'border-2 border-primary',
              )}
              style={{
                background: 'linear-gradient(0deg, rgba(24, 27, 36, 0.35), rgba(24, 27, 36, 0.35))',
              }}
            >
              {!isDragging && hoverRender}
            </div>
            {!hideDetail && <AssetDetailIcon className='absolute' data={data} />}
            {!isNil(icon) && (
              <div className='absolute left-0 bottom-0'>
                <div className='w-5 h-5 rounded-[10px] p-[2px] bg-[#00000040] flex text-center justify-center'>
                  <div className='text-FFFFFFC0 text-center flex items-center'>{icon}</div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <div className='w-full h-4 mt-3 text-xs'>
        {isEditing ? (
          <div className='w-full truncate text-center'>
            <Input
              autoFocus
              className='h-4'
              maxLength={32}
              defaultValue={name}
              onPressEnter={handleNameChange}
              onBlur={handleNameChange}
            />
          </div>
        ) : (
          <div
            title={name}
            className={classNames('w-full truncate text-center', { 'cursor-pointer': canRename })}
            onDoubleClick={() => {
              canRename && setIsEditing(true);
            }}
          >
            {name}
          </div>
        )}
      </div>
    </div>
  );
}
